import { Box, Skeleton } from "@mui/material";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useTranslation } from "@localization/useTranslation.ts";
import { useSystemOverviewHook } from "./SystemOverview.hooks";
import { useSystemOverviewStyles } from "./SystemOverview.styles";
import DotLegalPaper from "../../common/components/dotLegalPaper/DotLegalPaper";
import SystemDeleteDialog from "./systemDeleteDialog/SystemDeleteDialog";
import UpdateSystemTemplateDialog from "../updateSystemTemplateDialog/UpdateSystemTemplateDialog";
import { isTemplateCustomer } from "@common/templateCustomerHelper.ts";
import SystemDocumentTab from "./systemDocuments/SystemDocumentsTab";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import GeneralTabContent from "./generalTabContent/GeneralTabContent";
import SystemControlProcedureDialog from "./systemControlProcedureDialog/SystemControlProcedureDialog";
import EntityRiskAreasDialog from "../../risk/entityRiskAreasDialog/EntityRiskAreasDialog";
import { DotLegalFullScreenSpinner, DotLegalPageHeader } from "@dotlegal/dotlegal-ui-components";
import SystemAuditTab from "./systemAuditTab/SystemAuditTab";
import SystemClassificationTab from "./systemClassificationTab/SystemClassificationTab";
import { getPlanTab } from "../../plan/planTab/PlanTab";
import PageHeaderEditEntityButton, {
    PageHeaderEditEntityButtonMenuItem,
} from "../../common/components/pageHeaderEditEntityButton/PageHeaderEditEntityButton.tsx";
import RelatedTasksTab from "../../taskManagement/relatedTasksTab/RelatedTasksTab.tsx";
import { EntityType } from "@common/Common.types.ts";
import EntityGraphDialog from "@common/components/entityGraph/EntityGraphDialog.tsx";
import { useEntityGraphMenuItem } from "@common/components/entityGraph/EntityGraphMenuItem.hooks.ts";
import { useAddTaskMenuItem } from "@common/components/addTaskMenuItem/AddTaskMenuItemContext.hooks.ts";
import { useGetRelatedTaskData } from "../../taskManagement/relatedTasksTab/RelatedTasksTab.hooks.ts";
import PlanTabs, { PlanTabContent } from "../../plan/planTab/PlanTabs.tsx";
import SystemAccessTab from "./systemAccessTab/SystemAccessTab.tsx";
import SystemProcessingActivitiesTab from "./systemProcessingActivitiesTab/SystemProcessingActivitiesTab.tsx";
import SystemSuppliersTab from "./systemSuppliersTab/SystemSuppliersTab.tsx";
import DotLegalTabs from "../../common/components/dotLegalTabs/DotLegalTabs.tsx";
import { TabContent } from "@common/components/dotLegalTabs/DotLegalTabs.types.ts";
import { SystemOverviewPaperTabEnum } from "./SystemOverview.types.ts";
import { toTopTabIndex } from "@common/hooks/useOverviewTopTab";
import { dotLegalTabPanelNoPadding } from "@common/components/dotLegalTabPanel/DotLegalTabPanel.tsx";
import AllSystemInfo from "./allSystemInfo/AllSystemInfo.tsx";
import SystemStepTab from "./stepTab/SystemStepTab.tsx";

export const topPaperMinHeight = 233;

function SystemOverview() {
    const { permissions } = useUserContext();
    const { translateString } = useTranslation();
    const { informationSecurityFeatures, vendorManagementFeatures, addonsFeatures, platformFeatures } = usePlanContext();
    const addTaskMenuItem = useAddTaskMenuItem();

    const {
        infoData,
        getBreadCrumbs,
        editUrl,
        showDeleteSystemDialog,
        setShowDeleteSystemDialog,
        deleteSystemSuccess,
        showEditSystemTemplateInfo,
        setShowEditSystemTemplateInfo,
        id,
        selectedTab,
        setSelectedTab,
        selectedTopTab,
        setSelectedTopTab,
        showInternalControlProcedureDialog,
        setShowInternalControlProcedureDialog,
        showRiskAreasDialog,
        setShowRiskAreasDialog,
        refetchSystemInfo,
    } = useSystemOverviewHook();

    const { entityGraphMenuItem, isEntityGraphOpen, closeEntityGraph } = useEntityGraphMenuItem();
    const { getTaskTabHeader } = useGetRelatedTaskData(id!);

    const styles = useSystemOverviewStyles();

    const getTabContent = () => {
        const content: Array<PlanTabContent> = [];

        content.push(
            getPlanTab(
                `${translateString("documents")} ${informationSecurityFeatures.systemDocuments && infoData && infoData.numberOfDocuments > 0 ? `(${infoData.numberOfDocuments})` : ""}`,
                getDocumentsTab(),
                0,
                informationSecurityFeatures.systemDocuments,
                translateString("upgradeInfoSecPlan")
            )
        );

        content.push({
            label: `${translateString("suppliers")}${infoData && infoData.numberOfSuppliers > 0 ? ` (${infoData.numberOfSuppliers})` : ""}`,
            content: <SystemSuppliersTab systemId={id!} />,
            index: 1,
        });

        content.push({
            label: `${translateString("access")}${infoData && infoData.numberOfUsersWithAccess > 0 ? ` (${infoData.numberOfUsersWithAccess})` : ""}`,
            content: <SystemAccessTab systemId={id!} />,
            index: 2,
        });

        content.push({
            label: `${translateString("processingActivities")}${infoData && infoData.numberOfProcessingActivities > 0 ? ` (${infoData.numberOfProcessingActivities})` : ""}`,
            content: <SystemProcessingActivitiesTab systemId={id!} />,
            index: 3,
        });

        if (vendorManagementFeatures.hasVendorManagement && permissions.canManageInternalAssessment) {
            content.push(
                getPlanTab(
                    `${translateString("audits")}${infoData && infoData.numberOfAudits > 0 ? ` (${infoData.numberOfAudits})` : ""}`,
                    <SystemAuditTab systemId={id!} systemName={infoData?.name ?? ""} />,
                    4,
                    vendorManagementFeatures.systemClassifications,
                    translateString("upgradeVendorManagementPlan")
                )
            );
        }

        if (vendorManagementFeatures.hasVendorManagement && (permissions.canManageGroupEntities || permissions.companyPermissions)) {
            content.push(
                getPlanTab(
                    `${translateString("classifications")}${infoData && infoData.numberOfClassifications > 0 ? ` (${infoData.numberOfClassifications})` : ""}`,
                    <SystemClassificationTab systemId={id!} />,
                    5,
                    vendorManagementFeatures.systemClassifications,
                    translateString("upgradeVendorManagementPlan")
                )
            );
        }

        if (permissions.taskPermissions.read && !addonsFeatures.research) {
            content.push(
                getPlanTab(
                    getTaskTabHeader(),
                    <RelatedTasksTab forEntityId={id!} forEntityType={EntityType.System} forEntityName={infoData?.name ?? ""} orderBy="deadline" />,
                    6,
                    platformFeatures.canAccessRelationsBetweenEntities,
                    translateString("upgradePrivacyPlanToday")
                )
            );
        }

        return content;
    };

    const getDocumentsTab = () => {
        return <SystemDocumentTab systemId={id} systemName={infoData?.name} refetch={() => refetchSystemInfo()} />;
    };

    const getPaperWithContent = () => {
        return (
            <Box sx={{ "& .MuiPaper-root": { borderTopLeftRadius: 0, marginTop: -dotLegalTabPanelNoPadding } }}>
                <DotLegalPaper>
                    <GeneralTabContent systemId={id} systemName={infoData?.name ?? ""} />
                </DotLegalPaper>
            </Box>
        );
    };

    const getTopTabsContent = () => {
        const tabs: Array<TabContent> = [
            {
                label: translateString("overview"),
                content: getPaperWithContent(),
                index: SystemOverviewPaperTabEnum.Main,
            },
            {
                label: translateString("allSystemInfo"),
                content: (
                    <Box sx={{ "& .MuiPaper-root": { borderTopLeftRadius: 0, marginTop: -dotLegalTabPanelNoPadding } }}>
                        <DotLegalPaper>
                            <AllSystemInfo systemId={id!} />
                        </DotLegalPaper>
                    </Box>
                ),
                index: SystemOverviewPaperTabEnum.AllSystemInfo,
            },
        ];

        if (infoData && infoData.templateSteps.length > 0) {
            infoData.templateSteps.forEach((step) => {
                tabs.push({
                    label: step.name,
                    content: (
                        <Box sx={{ "& .MuiPaper-root": { borderTopLeftRadius: 0, marginTop: -dotLegalTabPanelNoPadding } }}>
                            <DotLegalPaper>
                                <SystemStepTab systemId={id!} stepId={step.id} name={step.name} />
                            </DotLegalPaper>
                        </Box>
                    ),
                    index: toTopTabIndex(step.order),
                });
            });
        }

        return tabs;
    };

    const getEditMenuItems = () => {
        let menuItems: Array<PageHeaderEditEntityButtonMenuItem> = [];

        menuItems.push({
            menuItem: translateString("editSystem"),
            onClick: () => {},
            href: editUrl,
        });

        if (permissions.systemPermissions.delete) {
            menuItems.push({
                menuItem: translateString("deleteSystem"),
                onClick: () => setShowDeleteSystemDialog(true),
            });
        }

        if (permissions.canManageCustomers && isTemplateCustomer()) {
            menuItems.push({
                menuItem: translateString("editSystemTemplate"),
                onClick: () => setShowEditSystemTemplateInfo(true),
            });
        }

        if (!addonsFeatures.research) {
            menuItems.push({
                menuItem: translateString("systemInternalControlProcedureHeader"),
                onClick: () => setShowInternalControlProcedureDialog(true),
                planMenuItemInfo: {
                    hasAccess: informationSecurityFeatures.internalControlProcedures,
                    menuName: translateString("systemInternalControlProcedureHeader"),
                    tooltip: translateString("upgradeInfoSecPlan"),
                },
            });
        }

        if (permissions.canAccessEnterpriseRiskManagement) {
            menuItems.push({
                menuItem: translateString("manageRiskAreas"),
                onClick: () => setShowRiskAreasDialog(true),
            });
        }

        addTaskMenuItem.addCreateTaskMenuItem(menuItems, {
            fromEntityId: id!,
            fromEntityType: EntityType.System,
            toEntityType: EntityType.Task,
        });

        menuItems.push(entityGraphMenuItem);

        return menuItems;
    };

    return (
        <Box sx={styles.container}>
            <DotLegalPageHeader breadCrumbs={getBreadCrumbs()} rightComponent={<PageHeaderEditEntityButton menuItems={getEditMenuItems()} />} />

            {!infoData ? (
                <>
                    <Box sx={{ display: "flex" }}>
                        <Skeleton variant="rounded" width={100} height={42} sx={{ borderRadius: "20px 0 0 0" }} />
                        <Skeleton variant="rounded" width={160} height={42} sx={{ borderRadius: "0 20px 0 0" }} />
                    </Box>
                    <Box sx={{ "& .MuiPaper-root": { borderTopLeftRadius: 0 } }}>
                        <DotLegalPaper>
                            <Box sx={{ height: topPaperMinHeight }}>
                                <DotLegalFullScreenSpinner />
                            </Box>
                        </DotLegalPaper>
                    </Box>
                </>
            ) : (
                (() => {
                    const tabContent = getTopTabsContent();
                    const validatedSelectedTab = tabContent.some((t) => t.index === selectedTopTab) ? selectedTopTab : SystemOverviewPaperTabEnum.Main;
                    return (
                        <DotLegalTabs
                            ignoreTextTransformation
                            tabStyle="paper-tabs"
                            selectedTab={validatedSelectedTab}
                            setSelectedTab={setSelectedTopTab}
                            content={tabContent}
                            fullWidth={false}
                            noPadding
                        />
                    );
                })()
            )}

            <Box sx={styles.tableContainer}>
                <PlanTabs
                    fullWidth={false}
                    tabStyle="secondary"
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    content={getTabContent()}
                    noPadding
                    ignoreTextTransformation
                />
            </Box>

            {showDeleteSystemDialog && (
                <SystemDeleteDialog
                    systemId={infoData!.id}
                    systemName={infoData!.name}
                    onCloseDialog={() => setShowDeleteSystemDialog(false)}
                    onSuccessDelete={() => deleteSystemSuccess()}
                />
            )}

            {showEditSystemTemplateInfo && <UpdateSystemTemplateDialog systemId={infoData!.id} closeDialog={() => setShowEditSystemTemplateInfo(false)} />}

            {showInternalControlProcedureDialog && <SystemControlProcedureDialog onDialogClose={() => setShowInternalControlProcedureDialog(false)} />}

            {showRiskAreasDialog && <EntityRiskAreasDialog entityType={EntityType.System} entityId={id!} onClose={() => setShowRiskAreasDialog(false)} />}

            {isEntityGraphOpen && id && <EntityGraphDialog rootType={EntityType.System} rootId={id} onClose={closeEntityGraph} />}
        </Box>
    );
}

export default SystemOverview;
